import React from 'react';
import { Grid, makeStyles } from '@material-ui/core';

import Typography from 'components/Typography';
import { DATE_ISO_SHORT } from 'constants/dates';
import {
  EngineerCalendarDay,
  useEngineerCalendarStyles
} from './EngineerCalendarBody';
import { EngineerCalendarContext } from './EngineerCalendar';

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex'
    },
    availabilityCell: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    availabilityFraction: {
      color: theme.palette.aegis.semantic.info.base
    }
  }),
  { name: 'EngineerCalendarAvailability' }
);

function EngineerCalendarAvailabilityCell({
  children = null,
  testId
}: {
  children?: React.ReactNode;
  testId: string;
}) {
  const classes = useStyles();
  const calendarClasses = useEngineerCalendarStyles();

  return (
    <Grid
      container
      className={`${calendarClasses.cell} ${classes.availabilityCell}`}
      data-testid={testId}
    >
      {children}
    </Grid>
  );
}

function NoDataAvailable({ testId }: { testId: string }) {
  return (
    <EngineerCalendarAvailabilityCell testId={testId}>
      <Typography>No</Typography> <Typography>data</Typography>{' '}
      <Typography>available</Typography>
    </EngineerCalendarAvailabilityCell>
  );
}

function EngineerCalendarAvailability({
  days
}: {
  days: EngineerCalendarDay[];
}) {
  const { calendarAvailability } = React.useContext(EngineerCalendarContext);
  const classes = useStyles();

  const testIdPrefix = 'engineerCalendarAvailabilityUtilisation';

  return (
    <div className={classes.root} data-testid={`${testIdPrefix}_root`}>
      {days?.map(day => {
        const key = `${day.labels.day}-availability`;
        const dayAvailabilityTestId = `${testIdPrefix}_${day.labels.day}`;

        if (
          (!calendarAvailability.data || calendarAvailability.isLoading) &&
          !calendarAvailability.error
        ) {
          return (
            <EngineerCalendarAvailabilityCell
              testId={dayAvailabilityTestId}
              key={key}
            />
          );
        }

        // Requests failed or there's no availability data
        if (
          calendarAvailability.error ||
          calendarAvailability.data?.length === 0
        ) {
          return <NoDataAvailable testId={dayAvailabilityTestId} key={key} />;
        }

        const currentAvailability = calendarAvailability.data?.find(
          availabilityDay =>
            availabilityDay.date === day.date.toFormat(DATE_ISO_SHORT)
        );
        // Engineer isn't working for that day
        if (!currentAvailability || currentAvailability.availability === 0) {
          return (
            <EngineerCalendarAvailabilityCell
              testId={dayAvailabilityTestId}
              key={key}
            >
              <Typography>Not</Typography> <Typography>working</Typography>
            </EngineerCalendarAvailabilityCell>
          );
        }

        const {
          utilisation,
          availability,
          percentageUtilisation
        } = currentAvailability;

        // No availability data
        if (
          utilisation === undefined ||
          availability === undefined ||
          percentageUtilisation === undefined
        ) {
          return <NoDataAvailable testId={dayAvailabilityTestId} key={key} />;
        }

        return (
          <EngineerCalendarAvailabilityCell
            testId={dayAvailabilityTestId}
            key={key}
          >
            <Typography
              variant="h3"
              component="div"
              className={classes.availabilityFraction}
            >
              {utilisation}/{availability}
            </Typography>
            <Typography>{percentageUtilisation}%</Typography>
          </EngineerCalendarAvailabilityCell>
        );
      })}
    </div>
  );
}

export default EngineerCalendarAvailability;
